import { Color } from '@tinderapp/ui-utils';
import memoize from 'fast-memoize';
import invariant from 'invariant';

import { findLast } from 'Lib/utils/lodashUtils';

import {
  FILL_TYPE_ANGULAR_GRADIENT,
  FILL_TYPE_LINEAR_GRADIENT,
  FILL_TYPE_RADIAL_GRADIENT,
  SPREAD_METHOD_PAD,
  SPREAD_METHOD_REFLECT,
  SPREAD_METHOD_REPEAT,
} from './constants';
import type {
  AngularGradientFill,
  ColorStopObject,
  Fill,
  FillType,
  LinearGradientFill,
  RadialGradientFill,
  SpreadMethod,
  UnsanitizedAngularGradientProps,
  UnsanitizedFillPropsMap,
  UnsanitizedLinearGradientProps,
  UnsanitizedRadialGradientProps,
} from './types';

export const DEGREES_TO_RADIANS = Math.PI / 180;

export const forcePercent = (num: number | string) =>
  typeof num === 'number' ? `${num * 100}%` : num;

/**
 * If an array of gradient stops is not specified directly, this generates one using from/to values
 *
 * @param from - start color
 * @param to - end color
 * @param stops - gradient color stops to use directly
 * @return gradient color stops
 */
export function normalizeGradientStops(
  from: string,
  to: string,
  stops: (string | ColorStopObject)[]
): ColorStopObject[] {
  return (
    processStops(stops) || [
      {
        offset: 0,
        color: from,
      },
      {
        offset: 1,
        color: to,
      },
    ]
  );
}

/**
 * Converts an angle to a point
 *
 * @param angle - angle in degrees
 * @return [x, y]
 */
export function angleToPoint(angle: number) {
  const x = 0.5 + Math.sin(angle * DEGREES_TO_RADIANS);
  const y = 0.5 - Math.cos(angle * DEGREES_TO_RADIANS);
  return [x, y] as const;
}

/**
 * Maps [0, 1] to colors in a black-to-white gradient
 *
 * @param i - gradient offset
 * @return [r, g, b, a]
 */
export const defaultGetColor: GetColorFunction = (i: number) => {
  const c = 255 * i;
  return [c, c, c, 255];
};

type GetColorFunction = (offset: number) => [number, number, number, number];

/**
 * Creates a memoized function for looking up gradient colors
 *
 * @param stops - color stops
 * @param [scale] - scale of the
 * @param [spreadMethod] - determines behavior outside the [0, 1] range
 * @return generates the color at a point in the gradient
 */
export function createGetColor(
  stops: ColorStopObject[],
  scale: number = 1,
  spreadMethod: SpreadMethod = SPREAD_METHOD_PAD
): GetColorFunction {
  const sortedColors = stops.sort((a, b) => a.offset - b.offset);
  const firstStop = sortedColors[0];
  const lastStop = sortedColors[sortedColors.length - 1];
  return memoize((offset: number) => {
    let i = offset / scale;
    if (i < 0 || i > 1) {
      switch (spreadMethod) {
        case SPREAD_METHOD_REFLECT:
          if (Math.floor(i) % 2 === 1) {
            i = -i;
          }
          i %= 1;
          if (i < 0) {
            i += 1;
          }
          break;
        case SPREAD_METHOD_REPEAT:
          if (i !== 0) {
            i %= 1;
          }
          if (i <= 0 && offset) {
            i += 1;
          }
          break;
        case SPREAD_METHOD_PAD:
        default:
          break;
      }
    }
    const leftStop = findLast(sortedColors, (stop) => i >= stop.offset) || firstStop;
    const rightStop = sortedColors.find((stop) => i <= stop.offset) || lastStop;
    const amount =
      rightStop.offset !== leftStop.offset
        ? (i - leftStop.offset) / (rightStop.offset - leftStop.offset)
        : 1;
    const [r, g, b, a] = new Color(leftStop.color)
      .mix(new Color(rightStop.color), amount)
      .rgbaTuple();
    return [r, g, b, a * 255];
  });
}

/**
 * Draws an angular gradient to canvas and returns the base64-encoded URL
 *
 * @param size - canvas width and height
 * @param getColor - function to generate a color from a number in the interval [0, 1]
 * @param angle - start angle
 * @return canvas with the gradient drawn on it
 */
export function drawAngularGradient(
  size: number,
  getColor: GetColorFunction = defaultGetColor,
  angle: number = 0
): HTMLCanvasElement {
  const offset = (-angle / 360) % 1;
  const factor = Math.PI * 2;
  const halfSize = size / 2;
  const canvas = document.createElement('canvas');

  canvas.width = size;
  canvas.height = size;

  const ctx = canvas.getContext('2d');
  invariant(ctx !== null, 'Could not get canvas context');

  const imageData = ctx.createImageData(size, size);
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const i = 4 * (x + y * size);
      const a = 0.5 - Math.atan2(x - halfSize, y - halfSize) / factor;
      const color = getColor((1 + offset + a) % 1);
      imageData.data.set(color, i);
    }
  }
  ctx.putImageData(imageData, 0, 0);
  return canvas;
}

/**
 * Ensures that all stops are an object with an offset and a color
 *
 * @param stops - color stops
 * @return color stops after processing, if necessary
 */
export function processStops(stops: (string | ColorStopObject)[]): ColorStopObject[] {
  const numStops = stops.length;

  return stops.map<ColorStopObject>((stop, i) => {
    // stop.offset == null should be unnecessary (no obvious null usages),
    // but leaving logic as-is just in case
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (typeof stop !== 'object' || stop.offset == null) {
      return {
        offset: i / (numStops - 1),
        color: typeof stop === 'object' ? stop.color : stop,
      };
    }
    return stop;
  });
}

export function sanitizeFillParams<
  TFillType extends FillType,
  TFill extends Fill = Extract<Fill, { type: TFillType }>
>(params: UnsanitizedFillPropsMap[TFillType], type: TFillType): TFill['props'] {
  // TypeScript is unable to discriminate the type of params
  // based on type, need to manually cast each instance of params
  // https://github.com/microsoft/TypeScript/issues/42384#issuecomment-1286159167
  switch (type) {
    case FILL_TYPE_LINEAR_GRADIENT:
      return sanitizeLinearGradientParams(params as UnsanitizedLinearGradientProps);
    case FILL_TYPE_RADIAL_GRADIENT:
      return sanitizeRadialGradientParams(params as UnsanitizedRadialGradientProps);
    case FILL_TYPE_ANGULAR_GRADIENT:
      return sanitizeAngularGradientParams(params as UnsanitizedAngularGradientProps);
    default:
      return params;
  }
}

export function sanitizeLinearGradientParams(
  params: UnsanitizedLinearGradientProps
): LinearGradientFill['props'] {
  const { angle, scale, spreadMethod } = params;
  return {
    angle,
    scale,
    stops: processStops(params.colors || params.stops),
    spreadMethod,
  };
}

export function sanitizeRadialGradientParams(
  params: UnsanitizedRadialGradientProps
): RadialGradientFill['props'] {
  const { cx, cy, r, fx, fy, fr, spreadMethod } = params;
  return {
    cx,
    cy,
    r,
    fx,
    fy,
    fr,
    stops: processStops(params.colors || params.stops),
    spreadMethod,
  };
}

export function sanitizeAngularGradientParams(
  params: UnsanitizedAngularGradientProps
): AngularGradientFill['props'] {
  const { angle, canvasSize, scale, slices, spreadMethod } = params;
  return {
    angle,
    canvasSize,
    stops: processStops(params.colors || params.stops),
    scale,
    slices,
    spreadMethod,
  };
}
